import { ref } from 'vue';

import {
  UNITS,
  WsMessageType,
  type ChatChannel,
  type ChatHistoryPayload,
  type ChatMessagePayload,
  type Village,
  type WsBattleReportPayload,
  type WsBuildingCompletePayload,
  type WsEnvelope,
  type WsNotificationPayload,
  type WsTickPayload,
  type WsTrainingCompletePayload,
} from '@waldermarch/shared';

import { VoiceLine, speak } from '@/audio/voice';
import { syncServerTime } from '@/composables/useClock';
import { play } from '@/audio';
import { playEffect } from '@/audio/effects';
import { useChatStore } from '@/stores/chat';
import { useRealmStore } from '@/stores/realm';
import { useUnreadStore } from '@/stores/unread';
import { useNotificationStore } from '@/stores/notifications';
import { useVillageStore } from '@/stores/village';
import { getToken } from '@/utils/api';
import { titleise } from '@/utils/format';

const WS_BASE =
  import.meta.env.VITE_WS_URL ??
  `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;

const connected = ref(false);
let socket: WebSocket | null = null;
let reconnectTimer: number | null = null;
let attempts = 0;
let deliberateClose = false;

function scheduleReconnect(): void {
  if (deliberateClose || reconnectTimer !== null) return;
  // Back off so a server that is down does not get hammered.
  const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempts, 5));
  attempts++;
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

function handle(raw: string): void {
  const villages = useVillageStore();
  const notifications = useNotificationStore();

  let envelope: WsEnvelope;
  try {
    envelope = JSON.parse(raw) as WsEnvelope;
  } catch {
    return;
  }

  syncServerTime(envelope.serverTime);

  switch (envelope.type) {
    case WsMessageType.HELLO:
    case WsMessageType.TICK: {
      const payload = envelope.payload as WsTickPayload;
      if (payload.villages) {
        villages.applyTick(
          payload.villages,
          envelope.serverTime,
          payload.armies,
          payload.training,
        );
      }
      break;
    }

    case WsMessageType.BUILDING_COMPLETE: {
      const payload = envelope.payload as WsBuildingCompletePayload;
      notifications.success(
        `${titleise(payload.buildingType)} finished at level ${payload.level}.`,
      );
      speak(VoiceLine.COMPLETE);
      play('notify');
      void villages.loadBuildings();
      void villages.loadSnapshot();
      /*
       * And the army, which is what actually unlocks the Train button.
       *
       * The tick refreshes the garrison and the drill queue, but never the
       * *roster*, and the roster is the list the village screen filters to
       * decide whether a building trains anything. So a barracks would finish,
       * announce itself, appear at level one, and still offer nothing to
       * recruit until the page was reloaded. It also carries unit stats, which
       * a smithy changes, so any completed building is a reason to look again.
       */
      void villages.loadArmy();
      // Tasks read the world, so the moment the world moves, whoever shows
      // tasks should look again rather than wait out a polling interval.
      window.dispatchEvent(new CustomEvent('quests:dirty'));
      break;
    }

    case WsMessageType.TRAINING_COMPLETE: {
      const payload = envelope.payload as WsTrainingCompletePayload;
      const name = UNITS[payload.unitType]?.name ?? titleise(payload.unitType);
      notifications.success(
        payload.count === 1 ? `${name} joins the garrison.` : `${payload.count} ${name} join the garrison.`,
      );
      // The counters come with the next tick; the roster and quests may not.
      void villages.loadArmy();
      window.dispatchEvent(new CustomEvent('quests:dirty'));
      break;
    }

    case WsMessageType.BATTLE_REPORT: {
      const payload = envelope.payload as WsBattleReportPayload;
      const { report } = payload;
      const defending = report.defender.villageId;
      const weDefended = villages.villages.some((v) => v.id === defending);
      const won = weDefended ? !report.attackerWon : report.attackerWon;
      notifications[won ? 'success' : 'warning'](
        `Battle at ${report.defender.villageName}: ${won ? 'victory' : 'defeat'}.`,
      );
      // The same horn that plays when an army marches out. A battle resolving
      // is at least as worth hearing as one being ordered, and it used to
      // happen in silence whether you won or lost.
      void playEffect('attack');
      useUnreadStore().bump();
      void villages.loadSnapshot();
      void villages.loadArmy();
      /*
       * Prestige moves when battles resolve, and the badge in the top bar
       * read from a profile that nothing battle-shaped ever refreshed: it
       * sat on its last polled value (or a plain 0 if the profile had never
       * loaded) until the next click or the minute poll happened to fix it.
       * The battle is the event that changes the number, so the battle is
       * what reloads it.
       */
      void useRealmStore().load(true);
      break;
    }

    case WsMessageType.NOTIFICATION: {
      const payload = envelope.payload as WsNotificationPayload;
      const level = payload.level === 'warning' ? 'warning' : payload.level;
      notifications[level](payload.message);
      // Something happened that the player did not ask for and is probably not
      // looking at: support landing, a title slipping. Worth a chime.
      play(level === 'error' || level === 'warning' ? 'error' : 'notify');
      break;
    }

    case WsMessageType.CHAT_HISTORY: {
      useChatStore().applyHistory(envelope.payload as ChatHistoryPayload);
      break;
    }

    case WsMessageType.CHAT: {
      useChatStore().receive((envelope.payload as ChatMessagePayload).message);
      break;
    }

    case WsMessageType.ERROR:
    default:
      break;
  }
}

/** Say something in a chat room. Silently dropped while the socket is down. */
export function sendChat(channel: ChatChannel, body: string): void {
  if (socket?.readyState !== WebSocket.OPEN) return;
  socket.send(JSON.stringify({ type: WsMessageType.CHAT, payload: { channel, body } }));
}

export function connect(): void {
  const token = getToken();
  if (!token || socket) return;

  deliberateClose = false;
  const url = `${WS_BASE}${WS_BASE.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
  socket = new WebSocket(url);
  useChatStore().sendVia(sendChat);

  socket.addEventListener('open', () => {
    connected.value = true;
    attempts = 0;
  });

  socket.addEventListener('message', (event) => handle(String(event.data)));

  socket.addEventListener('close', () => {
    connected.value = false;
    socket = null;
    scheduleReconnect();
  });

  socket.addEventListener('error', () => {
    // 'close' always follows, which is where reconnection is handled.
    socket?.close();
  });
}

export function disconnect(): void {
  deliberateClose = true;
  if (reconnectTimer !== null) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  socket?.close();
  socket = null;
  connected.value = false;
  attempts = 0;
  // Signing out must not leave the next player looking at these rooms.
  useChatStore().reset();
}

export function useWebSocket() {
  return { connected, connect, disconnect };
}
